import { ChevronRight } from 'lucide-react';
import { Link, Navigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@repo/ui/src/hooks/use-mobile';
import { useAuthStore } from '../../stores/authStore';
import { defaultSectionForRole, legacySettingsRedirect, settingsSectionsForRole, useIsSettingsNarrow } from './settingsNav';

/** `/settings` (no `:section`): a legacy `?tab=` redirects to its mapped
 *  section; otherwise a wide window jumps straight to the role default, while a
 *  narrow one (≤ SETTINGS_NARROW_MAX_PX) renders the grouped drill-in list. */
export function SettingsIndex() {
  const { t } = useTranslation('settings');
  const role = useAuthStore((s) => s.role);
  const isMobile = useIsMobile();
  const isNarrow = useIsSettingsNarrow(isMobile);
  const location = useLocation();

  const legacySection = legacySettingsRedirect(location.search);
  if (legacySection) {
    return <Navigate replace to={`/settings/${legacySection}`} />;
  }

  if (!isNarrow) {
    return <Navigate replace to={`/settings/${defaultSectionForRole(role)}`} />;
  }

  const groups = settingsSectionsForRole(role);

  return (
    <ul className="space-y-6">
      {groups.map((g) => (
        <li key={g.key}>
          <div className="px-1 pb-1 text-[11px] font-semibold uppercase tracking-wider text-surface-400">
            {t(g.label.key)}
          </div>
          <ul className="divide-y divide-surface-100 rounded-lg border border-surface-200 bg-surface-0">
            {g.items.map((i) => (
              <li key={i.key}>
                <Link
                  to={`/settings/${i.key}`}
                  className="flex min-h-[44px] items-center gap-3 px-3 py-2.5 hover:bg-surface-50"
                >
                  <i.icon className="size-4 shrink-0 text-surface-500" strokeWidth={1.8} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[13.5px] font-medium text-surface-900">{t(i.label.key)}</div>
                    <div className="truncate text-xs text-muted-foreground">{t(`sections.${i.key}.desc`)}</div>
                  </div>
                  <ChevronRight className="size-4 shrink-0 text-surface-400" strokeWidth={1.8} />
                </Link>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}
